import { useKeyPress } from 'ahooks'
import * as R from 'ramda'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Dropdown from 'components/uiKit/Dropdown'
import type { IKitControlProps, ValueType } from 'components/uiKit/KitTypes'
import { flatOptions } from 'components/uiKit/KitUtils'
import { isMixed } from 'components/uiKit/KitUtils'
import { EMPTY_ARRAY } from 'constants/commonConstans'
import { t } from 'services/Translation'

import type { IMenuRef } from '../Menu'
import type { ISelectContext } from './SelectContext'
import SelectContext from './SelectContext'
import SelectList from './SelectList'
import SelectRoot from './SelectRoot'
import type { SelectValueType, ISelectOption } from './SelectTypes'
import { filterOption, recursiveFilter, setFocusVisible, splitBy } from './SelectUtils'

export interface ISelectProps<V extends SelectValueType> extends IKitControlProps<V> {
  /**
   * The style type of the select component.
   */
  styleType?: 'base' | 'stroke' | 'ghost'

  /**
   * Determines if the select component should take up the full width of its container.
   */
  fluid?: boolean

  /**
   * The placeholder text to display when no option is selected.
   */
  placeholder?: string

  /**
   * Determines if the select component should be automatically focused when rendered.
   */
  autoFocus?: boolean

  /**
   * Determines if the select component allows multiple selections.
   */
  isMultiple?: boolean

  /**
   * Determines if the select component allows manual input.
   */
  inputable?: boolean

  /**
   * Determines if the select component supports search functionality.
   */
  isSearch?: boolean

  /**
   * Determines if the select component allows resetting the selected value to `null`.
   */
  resettable?: boolean

  /**
   * Determines if the select component is in a loading state.
   */
  loading?: boolean

  /**
   * The options to be displayed in the select component.
   */
  options?: ISelectOption<V>[]

  /**
   * Determines if the select component has an error state.
   */
  error?: boolean

  /**
   * The type of the input element when `inputable` is `true`.
   */
  inputType?: 'text' | 'number'

  /**
   * The type of the dropdown component.
   */
  dropdownType?: 'dropdown' | 'popover'

  /**
   * The separators to be used for multiple values when `isMultiple` is `true`.
   */
  separators?: string[]

  /**
   * Determines if empty list should be hidden.
   */
  hideEmptyList?: boolean

  /**
   * The minimum width of the select component.
   */
  minWidth?: number

  /**
   * Width of the select component.
   */
  width?: number

  /**
   * A function to validate each option in the select component.
   */
  optionValidator?: <V extends SelectValueType>(option: ISelectOption<V>) => boolean

  /**
   * The offset of the dropdown component.
   */
  offset?: [number, number]
}

const Select = React.forwardRef<HTMLDivElement, ISelectProps<SelectValueType>>(
  (props, outerRef) => {
    const localRef = useRef<HTMLInputElement>(null)
    const ref = (outerRef || localRef) as React.RefObject<HTMLDivElement>
    const inputRef = useRef<HTMLInputElement>(null)
    const listRef = useRef<IMenuRef<ValueType>>(null)

    const { defaultValue, value: valueProp = defaultValue, placeholder: placeholderProp } = props
    const { name, options, disabled, loading, readOnly, fluid, dropdownType, hideEmptyList } = props
    const { isMultiple, isSearch, separators, inputable, hidden, offset } = props
    const value = isMixed(valueProp) ? undefined : (valueProp as SelectValueType | undefined)
    const placeholder = isMixed(valueProp) ? t('input.mixed') : placeholderProp

    const { onChange, onBlur, onFocus } = props
    const [isOpen, setIsOpen] = useState(false)
    const [forceSearch, setForceSearch] = useState<boolean>(false)
    const [focus, setFocus] = useState(false)
    const [inputValue, setInputValue] = useState(
      inputable && !isMultiple ? String(value || '') : '',
    )

    const map = useMemo(() => R.indexBy((i) => String(i.value), flatOptions(options)), [options])
    const listOptions = useMemo(
      () =>
        isSearch && inputValue
          ? recursiveFilter(options, filterOption(inputValue))
          : options || EMPTY_ARRAY,
      [isSearch, inputValue, options],
    )
    const flattenOptions = useMemo(() => flatOptions(listOptions), [listOptions])
    const hideList = Boolean(hideEmptyList && !listOptions?.length)

    const handleMouseUp = useCallback(() => setFocusVisible(ref.current, 'off'), [ref])

    const handleChange = useCallback(
      (value: SelectValueType) => !disabled && !loading && onChange?.(value),
      [onChange, disabled, loading],
    )

    const handleSeparate = useCallback(
      (str?: string) => {
        const temp = (str ?? inputValue).trim()
        if (temp) {
          setInputValue('')
          const inputtedValue = separators ? splitBy(temp, separators) : [temp]
          const parsedValue = inputtedValue?.map((cur) => {
            const option = flattenOptions.find((item) => {
              return (
                String(item.value) === cur ||
                String(item.label) === cur ||
                String(item.textLabel) === cur ||
                String(item.searchable) === cur
              )
            })
            if (option) {
              return option.value
            }
            return cur
          }, [])

          const newValue = R.uniq([...((value || []) as []), ...parsedValue])
          handleChange?.(newValue)
        }
      },
      [handleChange, flattenOptions, inputValue, separators, value],
    )

    const handleFocus = useCallback(() => {
      if (!disabled) {
        onFocus?.()
        setFocus(true)
        inputRef.current?.focus()
        if (!focus) {
          setFocusVisible(ref.current, 'on')
        }
      }
    }, [ref, disabled, focus, onFocus])

    const handleBlur = useCallback(() => {
      if (!disabled) {
        onBlur?.()
        setFocus(false)
        setIsOpen(false)
        setForceSearch(false)
        setFocusVisible(ref.current, 'off')

        if (isMultiple && separators) {
          handleSeparate()
        }
      }
    }, [ref, disabled, isMultiple, separators, onBlur, handleSeparate])

    const handleChooseOption = useCallback(
      (item: ISelectOption<SelectValueType>) => {
        inputRef.current?.focus()
        setInputValue('')
        setForceSearch(false)
        if (!isMultiple && !item.disabled) {
          setInputValue?.(inputable ? String(item.value) : '')
          handleChange?.(item.value)
          setIsOpen(false)
        }
        if (isMultiple) {
          setInputValue?.('')
          if (Array.isArray(value)) {
            if (value.includes(item.value)) {
              handleChange?.(value.filter((v) => v !== item.value))
            } else if (!item.disabled) {
              handleChange?.([...value, item.value])
            }
          } else if (!item.disabled) {
            handleChange?.([item.value])
          }
        }
      },
      [handleChange, inputable, isMultiple, value],
    )

    useEffect(() => {
      if (!isMultiple && inputable) {
        setInputValue(String(value || ''))
      }
    }, [inputable, isMultiple, value])

    useKeyPress(['esc'], () => setIsOpen(false), { target: ref })

    useKeyPress(['enter', 'space', 'downarrow', 'uparrow'], () => setIsOpen(true), { target: ref })

    const data = useMemo(
      () => ({ map, listOptions, flattenOptions }),
      [map, listOptions, flattenOptions],
    )
    const state = useMemo(
      () => ({ isOpen, focus, forceSearch, inputValue, hideList }),
      [isOpen, focus, forceSearch, inputValue, hideList],
    )
    const actions = useMemo(() => ({ setIsOpen, setForceSearch, setInputValue }), [])
    const handlers = useMemo(
      () => ({ handleChange, handleChooseOption, handleSeparate, handleMouseUp }),
      [handleChange, handleChooseOption, handleSeparate, handleMouseUp],
    )
    const contextValue: ISelectContext<SelectValueType> = useMemo(
      () => ({
        ...props,
        ...state,
        ...data,
        placeholder,
        value,
        actions,
        handlers,
        listRef,
        inputRef,
        ref,
      }),
      [props, value, ref, data, state, actions, handlers, placeholder],
    )

    if (hidden) {
      return null
    }

    return (
      <SelectContext.Provider value={contextValue as unknown as ISelectContext<SelectValueType>}>
        <Dropdown
          disabled={readOnly || disabled}
          fluid={fluid}
          name={`${name}Dropdown`}
          offset={offset}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onVisibleChange={setIsOpen}
          overlay={hideList ? null : <SelectList rootRef={ref} />}
          rootRef={ref}
          styleType='clear'
          tabIndex={-1}
          type={dropdownType || 'popover'}
          visible={isOpen}
        >
          <SelectRoot />
        </Dropdown>
      </SelectContext.Provider>
    )
  },
)

Select.displayName = 'Select'

export default React.memo(Select) as <V extends SelectValueType>(
  props: ISelectProps<V>,
) => JSX.Element
